import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Lock } from "lucide-react";
import { PATIENT_GUIDE } from "@/lib/patient-guide";
import { bulletinShortDate, type Bulletin } from "@/lib/bulletin";

/**
 * Home masthead: "Canberra, today." Everything here comes from
 * lib/bulletin.ts (data only). Static twin: bulletinMarkup() in
 * scripts/prerender-markup.mjs; checked by scripts/fact-check.mjs.
 */
export function MorningBulletin({
  cityName,
  bulletin,
  updatedLabel,
  acceptingCount,
  noticesState,
}: {
  cityName: string;
  bulletin: Bulletin;
  updatedLabel: string | null;
  acceptingCount: number | null;
  noticesState: "loading" | "error" | "ready";
}) {
  const { lead } = bulletin;
  return (
    <section className="ds-bulletin" aria-labelledby="home-title">
      <header className="ds-bulletin__mast">
        <h1 id="home-title" className="ds-display-1">{cityName}, today.</h1>
        <p className="ds-bulletin__date">
          <span>{bulletin.dateLabel}</span>
          {updatedLabel ? <span className="ds-fine">{updatedLabel}</span> : null}
        </p>
      </header>

      <ul className="ds-conditions" aria-label={`Conditions in ${cityName}`}>
        {bulletin.conditions.map((item) => (
          <li key={item.key}>
            <Link to={item.to} data-fact={item.key}>
              <span className="ds-conditions__label">{item.label}</span>
              <span className="ds-conditions__value">{item.value}</span>
              <span className="ds-conditions__detail">{item.detail}</span>
            </Link>
          </li>
        ))}
      </ul>

      {bulletin.quietNote && noticesState === "ready" ? <p className="ds-bulletin__quiet">{bulletin.quietNote}</p> : null}

      {lead ? (
        <div className="ds-bulletin__news">
          <article className={`ds-lead-notice ds-lead-notice--${lead.tone}`} data-fact="notice">
            <p className="ds-lead-notice__eyebrow">{lead.eyebrow}</p>
            <h2 className="ds-lead-notice__title">{lead.title}</h2>
            {lead.summary ? <blockquote className="ds-lead-notice__quote">“{lead.summary}”</blockquote> : null}
            <p className="ds-lead-notice__foot">
              <a href={lead.sourceUrl ?? undefined} target="_blank" rel="noopener noreferrer">
                Read it on {lead.sourceLabel} <ArrowUpRight aria-hidden="true" />
              </a>
              <time dateTime={lead.publishedAt}>Published {bulletinShortDate(lead.publishedAt)}</time>
            </p>
          </article>

          {bulletin.also.length > 0 ? (
            <aside className="ds-also" aria-labelledby="home-also">
              <h2 id="home-also" className="ds-eyebrow">{bulletin.alsoHeading}</h2>
              <ol>
                {bulletin.also.map((notice) => (
                  <li key={`${notice.publishedAt}-${notice.title}`} data-fact="notice">
                    <time dateTime={notice.publishedAt}>{bulletinShortDate(notice.publishedAt)}</time>
                    <a href={notice.sourceUrl ?? undefined} target="_blank" rel="noopener noreferrer">{notice.title}</a>
                  </li>
                ))}
              </ol>
              <Link className="ds-link" to="/alerts">All public health notices</Link>
            </aside>
          ) : null}
        </div>
      ) : (
        <p className="ds-bulletin__quiet">
          {noticesState === "loading"
            ? "Checking official public health sources…"
            : noticesState === "error"
              ? <>Notices didn't load. <Link className="ds-link" to="/alerts">See all public health notices</Link></>
              : "No public health notices from official sources yet."}
        </p>
      )}

      <div className="ds-bulletin__row">
        <section className="ds-card ds-bulletin__card" aria-labelledby="bulletin-patients">
          <p className="ds-eyebrow ds-eyebrow--teal">For patients</p>
          <h2 id="bulletin-patients" className="ds-title">Which specialist do I need?</h2>
          <p className="ds-small">Find the kind of specialist who usually treats a condition. Your GP makes the referral.</p>
          <p className="ds-bulletin__links">
            <Link className="ds-link" to="/conditions">Condition guides</Link>
            <Link className="ds-link" to={PATIENT_GUIDE.path}>{PATIENT_GUIDE.title}</Link>
          </p>
        </section>
        <section className="ds-card ds-bulletin__card ds-bulletin__card--members" aria-labelledby="bulletin-rounds">
          <p className="ds-eyebrow ds-eyebrow--teal"><Lock aria-hidden="true" className="ds-inline-icon" /> In Rounds · members</p>
          <h2 id="bulletin-rounds" className="ds-title">Case questions, GP to specialist</h2>
          <p className="ds-small">Where {cityName} GPs and specialists ask and answer de-identified case questions. Members only: nothing from Rounds appears on the public site.</p>
          <p className="ds-bulletin__links"><Link className="ds-link" to="/board/rounds">Clinician sign in <ArrowRight aria-hidden="true" className="ds-inline-icon" /></Link></p>
        </section>
        <section className="ds-card ds-bulletin__card" aria-labelledby="bulletin-rooms">
          <p className="ds-eyebrow ds-eyebrow--teal">From the rooms</p>
          <h2 id="bulletin-rooms" className="ds-title" data-fact="accepting">
            {acceptingCount === null
              ? "Checking availability…"
              : acceptingCount === 0
                ? "No availability confirmed yet"
                : `${acceptingCount} specialist${acceptingCount === 1 ? "" : "s"} confirmed accepting referrals`}
          </h2>
          <p className="ds-small">Practices confirm their own availability. PulseBoard never guesses it.</p>
          <p className="ds-bulletin__links"><Link className="ds-link" to="/directory?type=specialist&accepting=yes">See who is accepting</Link></p>
        </section>
      </div>
    </section>
  );
}
